import { computed, inject, Signal } from '@angular/core';
import { withRequestState } from '@iris/common/signals/features/request-status.feature';
import { IrisDMSFolderOrFile, isFile, isFolder } from '@iris/modules/dms/utils/file.utils';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { removeAllEntities, removeEntities, removeEntity, setAllEntities, setEntities, setEntity, updateEntity, withEntities } from '@ngrx/signals/entities';
import { IrisDMSFile, IrisDMSFileI } from '../models/IrisDMSFile';
import { IrisDMSFolder, IrisDMSFolderI, IrisDmsRootFolder } from '../models/IrisDMSFolder';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { IrisFilesService } from '../services/files.service';
import { distinctUntilChanged, mergeMap, pipe, map, switchMap, tap, throwError, of, take, merge, filter, Observable, catchError, finalize } from 'rxjs';
import { IrisDmsFileInfoStore } from './file-info.signal-store';
import { IrisDmsGlobalStore } from './dms-global.signal-store';
import { IrisDmsFolderRequestStatusStore } from './folder-request-status.signal-store';
import { tapResponse } from '@ngrx/operators';
import { IrisDMSTagI } from '../models/IrisDMSTag';
import { IrisDmsBulkActionsService } from '../services/dms-bulk-actions.service';
import { IrisDmsFoldersSandbox } from '../sandbox/dms-folders.sandbox';
import { IrisConfirmationService } from '@iris/common/services/confirmation.service';
import { ToastService } from '@ngx-strakit/cdk/controls/toast';
import { TranslateService } from '@ngx-translate/core';
import { chain, difference, differenceBy, partition, uniq, uniqBy } from 'lodash';
import { IrisDmsContentPaginationI } from '../models/IrisDmsContentPagination';
import { IrisPageChangedEvent } from '../../grid/ag-grid/models/grid-pagination';
import { Store } from '@ngrx/store';
import { ReloadCurrentPage, SetSelectedFolder } from '../sandbox/actions/dms-global.actions';
import { DmsFileNamePart, getFileNamePart, getNewFileName } from '@iris/common/utils/file.utils';
import { LatLngLiteral } from 'leaflet';
import { IrisDmsSidebarStore } from './dms-sidebar.signal-store';
import { IrisDmsAlertsSandbox } from '../sandbox/dms-alerts.sandbox';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IrisConfirmRenameFileModalComponent } from '../components/modals/confirm-rename-file-modal/confirm-rename-file-modal.component';
import { IrisModal } from '@iris/common/models/IrisModal';
import { defaultContentPagination } from '../models/default-content-pagination';

export interface IrisDmsContentStateI {
  pagination: IrisDmsContentPaginationI;
  paginationTotal: number;
  selectedEntitiesIds: string[];
  selectedEntities: IrisDMSFolderOrFile[];
  unselectedEntitiesIds: string[];
  unselectedEntities: IrisDMSFolderOrFile[];
  fileUpdatePending: boolean;
  contentLoading: boolean;
  loadingFiles: string[];
}

export const initialState: IrisDmsContentStateI = {
  pagination: defaultContentPagination,
  paginationTotal: null,
  selectedEntitiesIds: [],
  selectedEntities: [],
  unselectedEntitiesIds: [],
  unselectedEntities: [],
  fileUpdatePending: false,
  contentLoading: true,
  loadingFiles: [],
};

export const IrisDmsContentStore = signalStore(
  { providedIn: 'root' },
  withEntities<IrisDMSFolderOrFile>(),
  withState<IrisDmsContentStateI>(initialState),
  withRequestState(),
  withComputed(store => ({
    files: computed(() => store.entities().filter(isFile).map(file => new IrisDMSFile(file as IrisDMSFileI))),
    selectedEntitiesCount: computed(() => (store.selectedEntitiesIds ?? []).length),
    selectedFiles: computed(() => {
      const entityMap = store.entityMap();
      return store.selectedEntities()
        .filter(entity => !!entity && isFile(entity))
        .map(entity => new IrisDMSFile((entityMap[entity.id] ?? entity) as IrisDMSFileI));
    }),
    selectedFolders: computed(() => {
      const entityMap = store.entityMap();
      return store.selectedEntities()
        .filter(entity => !!entity && isFolder(entity))
        .map(entity => new IrisDMSFolder((entityMap[entity.id] ?? entity) as IrisDMSFolderI));
    }),
    hasNextPage:
      computed(() => store.pagination().offset + store.pagination().limit < store.paginationTotal()),
  })),
  withComputed((
    store,
    dmsGlobalStore = inject(IrisDmsGlobalStore),
    dmsBulkActionsService = inject(IrisDmsBulkActionsService),
  ) => ({
    filesIds: computed(() => store.files().map(file => file.id)),
    selectedFilesIds: computed(() => store.selectedFiles().map(entity => entity.id)),
    selectedFoldersIds: computed(() => store.selectedFolders().map(entity => entity.id)),
    unselectedFilesIds: computed(() => {
      const entityMap = store.entityMap();
      return store.unselectedEntitiesIds().filter(entityId => !!entityMap[entityId] && isFile(entityMap[entityId]));
    }),
    unselectedFoldersIds: computed(() => {
      const entityMap = store.entityMap();
      return store.unselectedEntitiesIds().filter(entityId => !!entityMap[entityId] && isFolder(entityMap[entityId]));
    }),
    selectedFilesCount: computed(() => store.selectedFiles().length),
    anySelectedFileLocked: computed(() => (store.selectedFiles() ?? []).some(({ isLocked }) => isLocked)),
    anySelectedFileFavorite: computed(() => (store.selectedFiles() ?? []).some(({ isFavorite }) => isFavorite)),
    multipleFilesSelected: computed(() => store.selectedFiles().length > 1),
    bulkActionParams: computed(() => {
      const checkAll = dmsGlobalStore.checkAll();
      return dmsBulkActionsService.getBulkActionParams(checkAll, store.selectedEntitiesIds(), store.unselectedEntitiesIds());
    }),
  })),
  withMethods((
    store,
  ) => ({
    getEntityById: (entityId: string): Signal<IrisDMSFolderOrFile> => computed(() => store.entityMap()[entityId]),
    getEntitiesByIds: (entitiesIds: string[]): Signal<IrisDMSFolderOrFile[]> => computed(() => {
      const entityMap = store.entityMap();
      return entitiesIds.map(entityId => entityMap[entityId]).filter(Boolean);
    }),
    getFileById: (fileId: string): Signal<IrisDMSFileI> => computed(() => store.entityMap()[fileId] as IrisDMSFileI),
    isFileLoading: (fileId: string): boolean => store.loadingFiles().includes(fileId),
    remove: (entityId: string) => patchState(store, removeEntity(entityId)),
    setPagination: (pagePagination: IrisPageChangedEvent) => {
      const pagination = {
        offset: pagePagination.page * pagePagination.pageSize,
        limit: pagePagination.pageSize,
        orderBy: pagePagination.orderBy,
      };
      patchState(store, { pagination });
    },
    resetPagination: () => patchState(store, { pagination: defaultContentPagination, paginationTotal: null }),
    setPaginationTotal: (paginationTotal: number) => patchState(store, { paginationTotal }),
    setAllEntities: (entities: IrisDMSFolderOrFile[]) => patchState(store, setAllEntities(entities)),
    setEntities: (entities: IrisDMSFolderOrFile[]) => patchState(store, setEntities(entities)),
    updateEntity: (entity: Partial<IrisDMSFolderOrFile>) => patchState(store, updateEntity({ id: entity.id, changes: entity })),
    reset(onlyEntities = false): void {
      patchState(store, removeAllEntities());
      if (!onlyEntities) {
        patchState(store, initialState);
      }
    },
    startContentLoading: () => patchState(store, { contentLoading: true }),
    finishContentLoading: () => patchState(store, { contentLoading: false }),
  })),
  withMethods(store => ({
    selectEntities: (ids: string[], replace = false) => {
      const entitiesToSelect = store.getEntitiesByIds(ids)();
      const selectedEntities = replace ? [...entitiesToSelect] : uniqBy([...store.selectedEntities(), ...entitiesToSelect], 'id');
      const selectedEntitiesIds = replace ? [...ids] : uniq([...store.selectedEntitiesIds(), ...ids]);
      const unselectedEntities = differenceBy(store.unselectedEntities(), entitiesToSelect, 'id');
      const unselectedEntitiesIds = difference(store.unselectedEntitiesIds(), ids);

      patchState(store, { selectedEntities, selectedEntitiesIds, unselectedEntities, unselectedEntitiesIds });
    },
    deselectEntities: (ids: string[], checkAll: boolean) => {
      const selectedEntitiesIds = difference(store.selectedEntitiesIds(), ids);
      const selectedEntities = store.getEntitiesByIds(selectedEntitiesIds)();

      const unselectedEntitiesIds = checkAll
        ? uniq([...store.unselectedEntitiesIds(), ...ids])
        : store.unselectedEntitiesIds();
      const unselectedEntities = store.getEntitiesByIds(unselectedEntitiesIds)();
      patchState(store, { selectedEntities, selectedEntitiesIds, unselectedEntities, unselectedEntitiesIds });
    },
    toggleEntitiesSelection: (selectedIds: string[], deselectedIds: string[], checkAll: boolean) => {
      const selectedEntitiesIds = chain([...store.selectedEntitiesIds(), ...selectedIds])
        .uniq()
        .difference(deselectedIds)
        .value();
      const entitiesToSelect = store.getEntitiesByIds(selectedEntitiesIds)();
      const selectedEntities = uniqBy([...store.selectedEntities(), ...entitiesToSelect], 'id')
        .filter(entity => selectedEntitiesIds.includes(entity.id));

      const unselectedEntitiesIds = chain(checkAll ? [...store.unselectedEntitiesIds(), ...deselectedIds] : store.unselectedEntitiesIds())
        .uniq()
        .difference(selectedIds)
        .value();
      const entitiesToUnselect = store.getEntitiesByIds(unselectedEntitiesIds)();
      const unselectedEntities = uniqBy([...store.unselectedEntities(), ...entitiesToUnselect], 'id')
        .filter(entity => unselectedEntitiesIds.includes(entity.id));

      patchState(store, { selectedEntities, selectedEntitiesIds, unselectedEntities, unselectedEntitiesIds });
    },
    resetSelection: () => patchState(store, { selectedEntities: [], selectedEntitiesIds: [], unselectedEntities: [], unselectedEntitiesIds: [] }),
  })),
  withMethods((
    store,
    filesService = inject(IrisFilesService),
    dmsFolderRequestStatusStore = inject(IrisDmsFolderRequestStatusStore),
    fileInfoStore = inject(IrisDmsFileInfoStore),
    dmsBulkActionsService = inject(IrisDmsBulkActionsService),
    dmsFoldersSandbox = inject(IrisDmsFoldersSandbox),
    dialog = inject(MatDialog),
  ) => ({
    updateFile$: rxMethod<IrisDMSFileI>(
      pipe(
        switchMap(file => {
          patchState(store, { fileUpdatePending: true });
          dmsFolderRequestStatusStore.setInProgress(file.parentId);
          return filesService.updateFile(file).pipe(
            tapResponse({
              next: updatedFile => {
                fileInfoStore.updateFileInfo(updatedFile);
                dmsFolderRequestStatusStore.setSuccess(file.parentId);
                patchState(store, updateEntity<IrisDMSFolderOrFile>({ id: updatedFile.id, changes: { ...updatedFile } }));
              },
              error: (error) => {
                dmsFolderRequestStatusStore.setError(file.parentId);
                return throwError(() => error);
              },
              finalize: () => patchState(store, { fileUpdatePending: false }),
            }),
          );
        }),
      ),
    ),
    updateFileTags$: rxMethod<{ fileId: string; tags: IrisDMSTagI[] }>(
      pipe(
        mergeMap(({ fileId, tags }) => {
          return filesService.updateFileTags(fileId, tags).pipe(
            map(updatedFile => {
              fileInfoStore.updateFileInfo(updatedFile);
              patchState(store, setEntity<IrisDMSFolderOrFile>(new IrisDMSFile(updatedFile)));
            }),
          );
        }),
      ),
    ),
    moveFoldersAndFiles(
      selectedFolderId: string,
      targetFolderId: string,
      selectedEnititesIds?: string[],
      unselectedEntitiesIds?: string[],
    ): Observable<string[]> {
      dmsFolderRequestStatusStore.setInProgress(targetFolderId);
      dmsFoldersSandbox.getParentFolders(targetFolderId);
      const params = store.bulkActionParams();
      return dmsBulkActionsService.moveEntities(
        selectedFolderId,
        targetFolderId,
        [...selectedEnititesIds ? selectedEnititesIds : params[0]],
        [...unselectedEntitiesIds ? unselectedEntitiesIds : params[1]],
      ).pipe(
        take(1),
        map(() => [...params[0], ...params[1]]),
        finalize(() => dmsFolderRequestStatusStore.setSuccess(targetFolderId)),
      );
    },
    copyFoldersAndFiles(selectedFolderId: string, targetFolderId: string, entitiesToCopy?: string[]): Observable<IrisDMSFolderOrFile[]> {
      dmsFolderRequestStatusStore.setInProgress(targetFolderId);
      dmsFoldersSandbox.getParentFolders(targetFolderId);
      const params = store.bulkActionParams();
      return dmsBulkActionsService.copyEntities(
        selectedFolderId,
        targetFolderId,
        [...entitiesToCopy ? entitiesToCopy : params[0]],
        [...params[1]],
      ).pipe(
        tap(() => dmsFolderRequestStatusStore.setSuccess(targetFolderId)),
      );
    },
    removeFoldersAndFiles(selectedFolderId: string, entitiesToRemove: string[] = []): Observable<IrisDMSFolderOrFile[]> {
      dmsFolderRequestStatusStore.setInProgress(selectedFolderId);
      const params = store.bulkActionParams();
      return dmsBulkActionsService.removeEntities(
        selectedFolderId,
        [...entitiesToRemove?.length ? entitiesToRemove : params[0]],
        [...params[1]],
      ).pipe(
        tap(() => dmsFolderRequestStatusStore.setSuccess(selectedFolderId)),
      );
    },
    showConfirmRenameFileModal(fileName: string, newFileName: string) {
      const dialogRef: MatDialogRef<IrisConfirmRenameFileModalComponent, boolean> = dialog
        .open(IrisConfirmRenameFileModalComponent, {
          data: {
            fileName,
            newFileName,
          },
          maxWidth: '500px',
        });

      return IrisModal.fromDialogRef(dialogRef);
    },
  })),
  withMethods((
    store,
    filesService = inject(IrisFilesService),
    fileInfoStore = inject(IrisDmsFileInfoStore),
  ) => ({
    fetchFile$: rxMethod<string>(
      pipe(
        filter((fileId) => !store.loadingFiles().includes(fileId)),
        mergeMap(fileId => {
          patchState(store, { loadingFiles: [...store.loadingFiles(), fileId] });
          return filesService.getFileById(fileId).pipe(
            tapResponse({
              next: file => {
                fileInfoStore.updateFileInfo(file);
                patchState(store, setEntity<IrisDMSFolderOrFile>(new IrisDMSFile(file)));
              },
              error: err => throwError(() => err),
              complete: () => patchState(store, { loadingFiles: store.loadingFiles().filter(id => id !== fileId) }),
            }),
          );
        }),
      ),
    ),
    updateFileInfo$: rxMethod<IrisDMSFileI>(
      pipe(
        filter(() => !store.fileUpdatePending()),
        switchMap(fileInfo => {
          const storeFile = store.getFileById(fileInfo?.id)();
          const checkFileExistance = storeFile?.name !== fileInfo.name;
          return (checkFileExistance ? filesService.fileExist(storeFile.parentId, fileInfo.name) : of(false)).pipe(
            switchMap(exists => {
              if (exists) {
                const fileName = getFileNamePart(fileInfo.name, DmsFileNamePart.Name);
                const newFileName = getNewFileName([fileInfo.name], fileInfo, fileName);
                const { onSuccess$, onClose$ } = store.showConfirmRenameFileModal(fileInfo.name, newFileName);
                return merge(onSuccess$, onClose$).pipe(
                  take(1),
                  filter(Boolean),
                  map(() => new IrisDMSFile({ ...fileInfo, name: newFileName })),
                );
              }
              return of(fileInfo);
            }),
            tap(updatedFile => store.updateFile$(updatedFile)),
          );
        }),
      ),
    ),
  })),
  withMethods((
    store,
    filesService = inject(IrisFilesService),
    fileInfoStore = inject(IrisDmsFileInfoStore),
    dmsBulkActionsService = inject(IrisDmsBulkActionsService),
    dmsGlobalStore = inject(IrisDmsGlobalStore),
    dmsSidebarStore = inject(IrisDmsSidebarStore),
    dmsFolderRequestStatusStore = inject(IrisDmsFolderRequestStatusStore),
    dmsFoldersSandbox = inject(IrisDmsFoldersSandbox),
    dmsAlertsSandbox = inject(IrisDmsAlertsSandbox),
    confirmationService = inject(IrisConfirmationService),
    toastService = inject(ToastService),
    translateService = inject(TranslateService),
    rxStore = inject(Store),
  ) => ({
    fetchFilesByIds$: rxMethod<string[]>(
      pipe(
        distinctUntilChanged(),
        switchMap(filesIds => {
          return filesService.getFilesByIds(filesIds).pipe(
            map(files => {
              const selectedFolderId = dmsGlobalStore.selectedFolderId();
              const filesToAdd = files.map(file => new IrisDMSFile(file)).filter(file => file.parentId === selectedFolderId);
              if (fileInfoStore.id()) {
                const openedFileIndex = filesToAdd.findIndex(file => file.id === fileInfoStore.id());
                const openedFile = filesToAdd[openedFileIndex];
                fileInfoStore.updateFileInfo(openedFile);
                patchState(store, setEntity<IrisDMSFolderOrFile>(openedFile));
                filesToAdd.splice(openedFileIndex, 1);
              }
              patchState(store, setEntities<IrisDMSFolderOrFile>(filesToAdd));
            }),
          );
        }),
      ),
    ),
    updateFilesTags: (files: IrisDMSFileI[]) => files.forEach(file => store.updateFileTags$({ fileId: file.id, tags: file.tags })),
    renameFile: (file: IrisDMSFileI, fileName: string) => store.updateFileInfo$({ ...file, name: fileName }),
    saveFileTags$: rxMethod<{ fileId: string; tags: IrisDMSTagI[] }>(
      pipe(
        switchMap((action) => {
          store.updateFileTags$(action);
          return [];
        }),
      ),
    ),
    updateFileCoordinates$: rxMethod<{ fileId: string; coordinates: LatLngLiteral }>(
      pipe(
        switchMap(({ fileId, coordinates }) => {
          return filesService.updateFileCoordinates(fileId, coordinates).pipe(
            tap(updatedFile => {
              fileInfoStore.updateFileInfo(updatedFile);
              const changes: Partial<IrisDMSFolderOrFile> = { photoMetadata: updatedFile.photoMetadata };
              patchState(store, updateEntity({ id: updatedFile.id, changes }));
            }),
          );
        }),
      ),
    ),
    deleteFile$: rxMethod<string>(
      pipe(
        switchMap(fileId => {
          const file = store.getFileById(fileId)();
          const selectedFilesIds = store.selectedFilesIds();

          const parentFolder = dmsFoldersSandbox.getFolderById(file.parentId)();
          dmsFolderRequestStatusStore.setInProgress(parentFolder.id);
          if (file.removedDate) {
            toastService.success({ message: translateService.instant('text.dms.FilesDeletedInfo') });
          }
          store.remove(fileId);
          if (selectedFilesIds.includes(fileId)) {
            const ids = selectedFilesIds.filter(id => id !== fileId);
            store.selectEntities(ids, true);
          }
          return dmsBulkActionsService.removeEntities(file.parentId, [fileId], []).pipe(
            tapResponse({
              next: () => dmsFolderRequestStatusStore.setSuccess(file.parentId),
              error: (err) => {
                dmsFolderRequestStatusStore.setError(file.parentId);
                return throwError(() => err);
              },
              complete: () => rxStore.dispatch(ReloadCurrentPage()),
            }),
          );
        }),
      ),
    ),
    deleteFileVersion$: rxMethod<{ fileId: string; versionId: string }>(
      pipe(
        switchMap(({ fileId, versionId }) => {
          return filesService.deleteFileVersion(fileId, versionId).pipe(
            tap(() => {
              toastService.success({ message: translateService.instant('label.dms.FileRemoved') });
              store.fetchFile$(fileId);
            }),
          );
        }),
      ),
    ),
    deleteAttachment$: rxMethod<{ fileId: string; attachmentId: string }>(
      pipe(
        switchMap(({ fileId, attachmentId }) => {
          const file = store.getFileById(fileId)();
          return filesService.deleteAttachments(fileId, attachmentId).pipe(
            map(() => {
              toastService.success({ message: translateService.instant('label.dms.FileRemoved') });
              const updatedFile = { ...file, attachments: file.attachments.filter(attach => attach.id !==  attachmentId) };
              fileInfoStore.updateFileInfo(updatedFile);
              patchState(store, setEntity<IrisDMSFolderOrFile>(updatedFile));
            }),
          );
        }),
      ),
    ),
    removeFiles$: rxMethod<{ filesIds: string[]; isFilterActive: boolean }>(
      pipe(
        switchMap(({ filesIds, isFilterActive }) => {
          const selectedFolderId = isFilterActive ? IrisDmsRootFolder.id : dmsGlobalStore.selectedFolderId();

          if (filesIds.includes(dmsSidebarStore.selectedFileId())) {
            dmsSidebarStore.hideSidebar();
          }

          return store.removeFoldersAndFiles(selectedFolderId, filesIds).pipe(
            tap(() => {
              const folder = dmsFoldersSandbox.getFolderById(selectedFolderId)();
              if (!dmsGlobalStore.foldersTabSelected() || folder.isSystem || folder.isRecentFilesFolder) {
                const messageLabel = folder.isSystem ? 'text.dms.FilesDeletedInfo' : 'text.dms.RecentFilesRemovedInfo';
                toastService.success({ message: translateService.instant(messageLabel) });
              }

              store.selectEntities([], true);
              rxStore.dispatch(ReloadCurrentPage());
            }),
          );
        }),
      ),
    ),
    removeSelectedFoldersAndFiles$: rxMethod<void>(
      pipe(
        switchMap(() => {
          const entities = store.selectedEntities();
          const selectedFolderId = dmsGlobalStore.selectedFolderId();
          const selectedFolder = dmsFoldersSandbox.getFolderById(selectedFolderId)();
          dmsFolderRequestStatusStore.setInProgress(selectedFolder.id);
          const [removedFolders] = partition(entities, (row: IrisDMSFolderOrFile) => isFolder(row));

          if (removedFolders.length) {
            dmsFoldersSandbox.removeFoldersFromStore(removedFolders.map(({ id }) => id));
          }

          return store.removeFoldersAndFiles(selectedFolderId).pipe(
            tapResponse({
              next: () => store.selectEntities([], true),
              error: err => throwError(() => err),
              complete: () => rxStore.dispatch(ReloadCurrentPage()),
            }),
          );
        }),
      ),
    ),
    copySelectedFiles$: rxMethod<{
      filesIds: string[];
      targetFolderId: string;
      selectedFolderId: string;
    }>(
      pipe(
        switchMap(({ filesIds, targetFolderId, selectedFolderId }) => {
          dmsFoldersSandbox.getParentFolders(targetFolderId, true);
          dmsFolderRequestStatusStore.setInProgress(targetFolderId);
          return store.copyFoldersAndFiles(selectedFolderId, targetFolderId, filesIds).pipe(
            tap(() => {
              dmsFolderRequestStatusStore.setSuccess(targetFolderId);
            }),
          );
        }),
      ),
    ),
    copySelectedFoldersAndFiles$: rxMethod<{ targetFolderId: string; selectedFolderId: string }>(
      pipe(
        switchMap(({ targetFolderId, selectedFolderId }) => {
          return store.copyFoldersAndFiles(selectedFolderId, targetFolderId).pipe(
            switchMap(() => {
              dmsFoldersSandbox.expandFolder(targetFolderId);
              return [];
            }),
          );
        }),
      ),
    ),
    moveContentEntities$: rxMethod<{
      entitiesIds: string[];
      targetFolderId: string;
      selectedFolderId: string;
      selectTargetFolder?: boolean;
      showSuccessMessage?: boolean;
    }>(
      pipe(
        switchMap(({ entitiesIds, targetFolderId, selectedFolderId, selectTargetFolder }) => {
          const targetFolder = dmsFoldersSandbox.getFolderById(targetFolderId)();
          return confirmationService.show$({
            message: translateService.instant('text.dms.DocumentsMovedInfo', { folderName: targetFolder.name }),
          }).pipe(
            filter(Boolean),
            switchMap(() => {
              dmsFoldersSandbox.getParentFolders(targetFolderId, true);
              return store.moveFoldersAndFiles(selectedFolderId, targetFolder.id, entitiesIds, []).pipe(
                tap(() => {
                  store.selectEntities([], true);
                  if (selectTargetFolder) {
                    rxStore.dispatch(SetSelectedFolder({ folderId: targetFolderId, expandFoldersTree: true }));
                  } else {
                    rxStore.dispatch(ReloadCurrentPage());
                  }
                }),
              );
            }),
          );
        }),
      ),
    ),
    moveSelectedFoldersAndFiles$: rxMethod<{
      targetFolderId: string;
      selectedFolderId: string;
      undo: boolean;
      selectTargetFolder: boolean;
    }>(
      pipe(
        switchMap(({ selectedFolderId, targetFolderId, undo, selectTargetFolder }) => {
          const targetFolder = dmsFoldersSandbox.getFolderById(targetFolderId);
          return store.moveFoldersAndFiles(selectedFolderId, targetFolderId).pipe(
            switchMap((movedEntities) => {
              const folderName = translateService.instant(targetFolder.name);
              let result = of(null as unknown);
              if (undo) {
                result = dmsAlertsSandbox.showActionConfirmation$(
                  translateService.instant('text.dms.FoldersMovedInfo', { folderName }),
                );
              }

              return result.pipe(
                tap(() => {
                  store.selectEntities([], true);
                  dmsFoldersSandbox.removeFoldersFromStore(movedEntities);
                  if (selectTargetFolder) {
                    rxStore.dispatch(SetSelectedFolder({ folderId: targetFolderId, expandFoldersTree: true }));
                  } else {
                    rxStore.dispatch(ReloadCurrentPage());
                  }
                }),
                catchError(() => {
                  return store.moveFoldersAndFiles(targetFolderId, selectedFolderId, movedEntities).pipe(
                    map(() => {
                      rxStore.dispatch(SetSelectedFolder({ folderId: selectedFolderId }));
                    }),
                  );
                }),
              );
            }),
          );
        }),
      ),
    ),
    restoreFile$: rxMethod<string>(
      pipe(
        mergeMap(fileId => filesService.restoreFile(fileId)),
      ),
    ),
    translateFile$: rxMethod<{ language: string; filesIds: string[] }>(
      pipe(
        switchMap(({ language, filesIds }) => filesService.requestTranslation(language, filesIds)),
      ),
    ),
    removeRecentFiles$: rxMethod<string[]>(
      pipe(
        switchMap(recentFilesIds => {
          const messageKey = recentFilesIds.length === 1 ? 'text.dms.RecentFileRemovedInfo' : 'text.dms.RecentFilesRemovedInfo';
          toastService.success({ message: translateService.instant(messageKey) });
          patchState(store, removeEntities(recentFilesIds));
          store.selectEntities(store.selectedEntitiesIds().filter(id => !recentFilesIds.includes(id)), true);
          store.setPaginationTotal(store.entities().length);
          return filesService.removeRecentFiles(recentFilesIds);
        }),
      ),
    ),
  })),
);
